import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/features/auth/store'
import {
  REVIEW_RATINGS,
  REVIEW_RATING_LABELS,
  REVIEW_STATUSES,
  REVIEW_STATUS_LABELS,
  createReview,
  exportReviews,
  formatPeriod,
  formatScore,
  listReviewEmployees,
  listReviews,
  type Review,
  type ReviewRating,
  type ReviewStatus,
} from '@/features/hr/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STATUS_TONE: Record<ReviewStatus, string> = {
  draft: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  submitted: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  approved: 'bg-success/10 text-success',
  closed: 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
}

/** Ratings run best to worst, so the colour does too. Grey for a review with no score. */
const RATING_TONE: Record<ReviewRating, string> = {
  exceeds: 'text-success',
  meets: 'text-success',
  below: 'text-amber-600 dark:text-amber-400',
  needs_improvement: 'text-amber-600 dark:text-amber-400',
  unsatisfactory: 'text-danger',
}

/** First day of the quarter that ended most recently, and its last day. */
function lastQuarter(today = new Date()): { start: string; end: string } {
  const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0)
  const start = new Date(endOfLastMonth.getFullYear(), endOfLastMonth.getMonth() - 2, 1)
  const iso = (value: Date) =>
    `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(
      value.getDate(),
    ).padStart(2, '0')}`
  return { start: iso(start), end: iso(endOfLastMonth) }
}

/**
 * Open a review on somebody.
 *
 * Only the two scores a human has to type are on this form. The sales and KPI halves are
 * computed from the orders — the detail screen has a button for it — and offering to type
 * them here would invite a manager to overwrite the measured number with an opinion
 * before anyone had seen it.
 */
function OpenDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const quarter = lastQuarter()
  const [employeeId, setEmployeeId] = useState('')
  const [start, setStart] = useState(quarter.start)
  const [end, setEnd] = useState(quarter.end)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const employees = useQuery({
    queryKey: ['review-employees'],
    queryFn: listReviewEmployees,
    retry: false,
    staleTime: 5 * 60_000,
    enabled: open,
  })

  const chosen = employees.data?.data.find((person) => person.id === employeeId)

  // An empty picker is a real answer, not a loading state. The endpoint is scoped the same
  // way the reviews are, so a manager whose reporting line is empty -- or a login with no
  // employee record behind it, which is what a role-demo account is -- gets nothing back.
  // Saying so beats a dropdown holding one placeholder and a button that never enables.
  const nobody = employees.isSuccess && employees.data.data.length === 0

  const create = useMutation({
    mutationFn: () =>
      createReview({
        employee_id: employeeId,
        review_period_start: start,
        review_period_end: end,
      }),
    onSuccess: (review) => {
      void queryClient.invalidateQueries({ queryKey: ['reviews'] })
      toast.success('Review opened. Pull the sales figures in next.')
      onClose()
      void navigate(`/hr/reviews/${review.id}`)
    },
    onError: (mutationError: unknown) => {
      setError(
        mutationError instanceof ApiError
          ? mutationError.message
          : 'Could not reach the server. Nothing was changed.',
      )
    },
  })

  const ready = Boolean(employeeId) && end > start

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Open a review"
      description="One review per person per period. The scores are filled in afterwards, not now."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!ready} loading={create.isPending}>
            Open
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="error">{error}</Alert>}

        {employees.isError && (
          <Alert tone="error">Could not load the list of people. Nothing was opened.</Alert>
        )}

        {nobody && (
          <Alert tone="info" title="Nobody here to review">
            You can only open a review on someone whose record you can see, and this account reaches
            none. That is usually a signed-in user with no employee record behind them, or a manager
            with nobody reporting to them yet.
          </Alert>
        )}

        <Field label="Employee">
          {(props) => (
            <select
              {...props}
              className={SELECT_CLASS}
              value={employeeId}
              disabled={nobody}
              onChange={(event) => setEmployeeId(event.target.value)}
            >
              <option value="">{nobody ? 'Nobody available' : 'Choose a person…'}</option>
              {(employees.data?.data ?? []).map((person) => (
                <option key={person.id} value={person.id}>
                  {person.employee_code} · {person.full_name} · {person.position}
                </option>
              ))}
            </select>
          )}
        </Field>

        {chosen && chosen.sales_target_monthly === null && (
          <Alert tone="info" title="No sales target on record">
            The sales third of this person&apos;s score has no denominator, so the review will stay
            unscored until somebody sets a monthly target on their employee record.
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Period from">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={start}
                onChange={(event) => setStart(event.target.value)}
              />
            )}
          </Field>
          <Field label="Period to" hint={end > start ? undefined : 'Must be after the start.'}>
            {(props) => (
              <Input
                {...props}
                type="date"
                value={end}
                onChange={(event) => setEnd(event.target.value)}
              />
            )}
          </Field>
        </div>
      </div>
    </Modal>
  )
}

/**
 * Every performance review, and where each one has got to.
 *
 * The overall score and the rating are the two columns anyone opens this screen for, so
 * they sit together and a review with neither says why rather than showing a blank: an
 * empty score is a review missing a component, not a person who scored nothing.
 */
export function PerformanceReviews() {
  const [status, setStatus] = useState<ReviewStatus | ''>('')
  const [rating, setRating] = useState<ReviewRating | ''>('')
  const [page, setPage] = useState(1)
  const [opening, setOpening] = useState(false)
  const can = useAuth((s) => s.can)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['reviews', { status, rating, page }],
    queryFn: () => listReviews({ status, rating, page }),
    retry: false,
  })

  const columns: Column<Review>[] = [
    {
      key: 'employee',
      header: 'Employee',
      className: 'font-medium',
      sortValue: (row) => row.employee_name ?? '',
      cell: (row) => (
        <Link to={`/hr/reviews/${row.id}`} className="hover:underline">
          {row.employee_name ?? 'Unnamed'}
          <span className="block text-xs font-normal text-slate-500 dark:text-slate-400">
            {row.employee_code} · {row.employee_position}
          </span>
        </Link>
      ),
    },
    {
      key: 'period',
      header: 'Period',
      className: 'whitespace-nowrap',
      sortValue: (row) => row.review_period_start,
      cell: (row) => formatPeriod(row.review_period_start, row.review_period_end),
    },
    {
      key: 'overall_score',
      header: 'Overall',
      className: 'text-right font-medium tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => (row.overall_score === null ? -1 : Number(row.overall_score)),
      cell: (row) => formatScore(row.overall_score),
    },
    {
      key: 'rating',
      header: 'Rating',
      sortValue: (row) => row.rating ?? '',
      cell: (row) =>
        row.rating ? (
          <span className={`text-sm font-medium ${RATING_TONE[row.rating]}`}>
            {REVIEW_RATING_LABELS[row.rating]}
          </span>
        ) : (
          <span className="text-sm text-slate-500 dark:text-slate-400">
            Insufficient data for evaluation period
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[row.status]}`}>
          {REVIEW_STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      key: 'signoff',
      header: 'Signed',
      className: 'whitespace-nowrap text-slate-500 dark:text-slate-400',
      cell: (row) => {
        const signed = [
          row.signoff_by_employee ? 'employee' : null,
          row.signoff_by_manager ? 'manager' : null,
        ].filter(Boolean)
        return signed.length ? signed.join(' + ') : '—'
      },
    },
  ]

  const meta = data?.meta

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Performance reviews</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Sales and KPI scores come from the orders. Attendance and the manager&apos;s judgement
            are typed in.
          </p>
        </div>
        {can('performance_reviews', 'create') && (
          <Button onClick={() => setOpening(true)}>Open a review</Button>
        )}
      </header>

      <Card>
        <CardHeader
          title="Reviews"
          description="A review with no overall score is missing one of its four components; the server will not let it be submitted until it has them all."
        />
        <DataTable
          rows={data?.data ?? []}
          columns={columns}
          getRowId={(row) => row.id}
          caption="Performance reviews"
          isLoading={isLoading}
          error={error}
          emptyMessage="No reviews to show."
          toolbar={
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="review-status">
                Status
              </label>
              <select
                id="review-status"
                className={SELECT_CLASS}
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value as ReviewStatus | '')
                  setPage(1)
                }}
              >
                <option value="">Every status</option>
                {REVIEW_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {REVIEW_STATUS_LABELS[value]}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="review-rating">
                Rating
              </label>
              <select
                id="review-rating"
                className={SELECT_CLASS}
                value={rating}
                onChange={(event) => {
                  setRating(event.target.value as ReviewRating | '')
                  setPage(1)
                }}
              >
                <option value="">Every rating</option>
                {REVIEW_RATINGS.map((value) => (
                  <option key={value} value={value}>
                    {REVIEW_RATING_LABELS[value]}
                  </option>
                ))}
              </select>

              <ExportButton
                filename="performance-reviews"
                selectClassName={SELECT_CLASS}
                onExport={(format) => exportReviews({ status, rating }, format)}
                errorMessage="Could not export the reviews."
              />
            </div>
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: setPage,
                  isFetching,
                  total: meta.total,
                }
              : undefined
          }
        />
      </Card>

      <OpenDialog open={opening} onClose={() => setOpening(false)} />
    </div>
  )
}
